import React, { useEffect, useState, useCallback } from 'react';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { withRouter, RouteComponentProps } from 'react-router';
import { PartnersAPI, SurveysAPI } from "../../API";

// Enum for page status
export enum PageStatus {
    None = 'NONE',
    Loading = 'LOADING',
    Loaded = 'LOADED',
    Error = 'ERROR',
    Submitting = 'SUBMITTING',
    Submitted = 'SUBMITTED'
}


export type FormValue = {
    name?: string;
    description?: string;
    partnerId?: string;
    surveyId?: string;
    successUrl: string;
    overQuotaUrl: string;
    disqualifiedUrl: string;
    qualityTerminatedUrl: string;
    statusCallbackUrl?: string;
    quota: string;
    surveyType?: string;
    surveyStatus?: string;
    vendorCPI?: string;
    preScreenVisibility?: boolean;
    marketMode?: 'all' | 'specific';
    selectedMarkets?: string[];
    // Per-question visibility — array of question_ids the partner can see.
    // Only meaningful when preScreenVisibility is true.
    visibleQuestionIds?: string[];
    useProxyUrl?: boolean;
    verisoul?: boolean;
    verisoul_risk_threshold?: number;
    verisoul_terminate_count?: number;
};

type ChildPrescreenConfig = {
    // Unique child survey id — the market identity for the partner→market
    // contract (selectedMarkets holds ids). shortName is display-only and
    // collides for same-country duplicates (two "JP"); id does not.
    id: string;
    shortName: string;
    // Free-form per-child label, used to disambiguate same-country duplicates
    // in the prescreen-question section header. Optional (legacy children NULL).
    childProjectName?: string;
    country: string;
    preQuestionsYN: 'Y' | 'N';
    questions: Array<{ question_id: string; question: string; questionType: string; type: string }>;
};

interface FormProps extends InjectedFormProps<FormValue>, RouteComponentProps {
    onClose: () => void;
    onSubmit: () => void;
    show: boolean;
    partnerId: string;
    partnerName: string;
    surveyId: string;
    surveyStatus: string;
    modalType: string;
    clientsQuota: any;
    // Per-child market options for the "Add to specific markets" checkbox list.
    // `id` is the child survey id (the selectedMarkets token — id-based contract);
    // `childProjectName` disambiguates same-country duplicates in the label.
    // shortName/value are display-only. id may be absent for unsaved CREATE picks
    // (partners only attach to saved children, so those rows are not selectable).
    childCountries?: { label: string; value: string; shortName: string; id?: string; childProjectName?: string }[];
    childPrescreenConfigs?: ChildPrescreenConfig[];
    // Existing parent/child signal (from PartnersTab). When false (modal opened
    // from a CHILD project's Partners tab) the "Add to all markets" radio is
    // hidden and the form defaults to "specific". Defaults to true (parent) so
    // behavior is unchanged anywhere the prop isn't supplied.
    isParentSurvey?: boolean;
    // Parent project name (e.g. "TEST-6767"). Used by the pre-screening visibility
    // list to strip the `${projectName}_${shortName}_` prefix from each child's
    // childProjectName, rendering just the PM-typed suffix in the section header.
    projectName?: string;
    isUniqueLinks?: boolean;
    // True when this is a parent with at least one child market. Its partner
    // quota has no userLimitCommitted cap (the backend enforces the floor:
    // the total of the child markets) and is optional when adding a partner.
    hasChildMarkets?: boolean;
}

// Mirrors the suffix derivation in PartnersTab.tsx / ChildSurveyTab.tsx — returns
// the PM-typed suffix or '' when the stored value is the bare base prefix, missing,
// or in an unknown shape. Callers gate parens on the empty-string result.
function extractDisplaySuffix(fullName: string | null | undefined, parentName: string, shortName: string): string {
    if (!fullName) return '';
    const trimmed = String(fullName).trim();
    if (!trimmed) return '';
    const base = `${parentName}_${shortName}`;
    const fullPrefix = `${base}_`;
    if (trimmed === base) return '';
    if (trimmed.startsWith(fullPrefix)) return trimmed.substring(fullPrefix.length);
    const orphanBase = `_${shortName}`;
    const orphanPrefix = `_${shortName}_`;
    if (trimmed === orphanBase) return '';
    if (trimmed.startsWith(orphanPrefix)) return trimmed.substring(orphanPrefix.length);
    
    // Fallback: If parent name changed, the string might be `OldParent_IN_Suffix`.
    const fallbackPrefix = `_${shortName}_`;
    const lastIndex = trimmed.lastIndexOf(fallbackPrefix);
    if (lastIndex !== -1) {
        return trimmed.substring(lastIndex + fallbackPrefix.length);
    }
    if (trimmed.endsWith(`_${shortName}`)) return '';
    
    return '';
}

const Form: React.FC<FormProps> = ({
    handleSubmit,
    onSubmit,
    onClose,
    show,
    partnerId,
    surveyId,
    initialize,
    partnerName,
    surveyStatus,
    modalType,
    reset,
    clientsQuota,
    childCountries = [],
    childPrescreenConfigs = [],
    isParentSurvey = true,
    projectName = '',
    isUniqueLinks,
    hasChildMarkets = false,
}) => {
    // Parent tab, adding a partner: blank quota = total of the child markets.
    const quotaOptional = hasChildMarkets && modalType === 'create';
    const [status, setStatus] = useState < PageStatus > (PageStatus.None);
    const [error, setError] = useState < string | null > (null);
    const [formValues, setFormValues] = useState < FormValue > ({
        partnerId,
        surveyId,
        successUrl: '',
        overQuotaUrl: '',
        disqualifiedUrl: '',
        qualityTerminatedUrl: '',
        statusCallbackUrl: '',
        quota: '',
        surveyType: surveyStatus,
        vendorCPI: '',
        preScreenVisibility: false,
        // Child context has no "all markets" option, so default to "specific"
        // there (parent keeps the original "all" default).
        marketMode: isParentSurvey ? 'all' : 'specific',
        selectedMarkets: [],
        visibleQuestionIds: [],
        verisoul: false,
    });
    // Max partner quota from the backend (getPartnerQuotaLimit); null = no cap
    // (a parent — its floor is enforced by the backend).
    const [quotaLimit, setQuotaLimit] = useState < number | null > (null);

    useEffect(() => {
        if (partnerId) {
            fetchDetails();
        }
    }, [partnerId]);

    const fetchDetails = useCallback(async () => {
        setStatus(PageStatus.Loading);
        try {
            if (!partnerId) throw new Error('Invalid ID');
            const data = await PartnersAPI.getUrlOnTheSurveyBasis(partnerId,surveyId);
            const leftQuotaPoints = await SurveysAPI.getLeftQuotaPoints({id: surveyId});
            if(!leftQuotaPoints.country){ alert("You haven't provided any DX Quota as the selected country is India."); }
            // Hey sonarqube! please ignore below code
            // setQuotaLimit(data.quota ? parseInt(data.quota) + leftQuotaPoints.countLeft : leftQuotaPoints.countLeft);
            // SCRUM-86: the same max the backend validates against — for a child
            // that shares the parent budget it is parent quota − the other
            // children's quotas, not this survey's userLimitCommitted.
            const limit = await PartnersAPI.getPartnerQuotaLimit(partnerId, surveyId);
            setQuotaLimit(limit && typeof limit.max === 'number' ? limit.max : null);
            const initialValues: FormValue = {
                name: data.name || '',
                description: data.description || '',
                partnerId,
                surveyId,
                successUrl: data.successUrl || '',
                overQuotaUrl: data.overQuotaUrl || '',
                disqualifiedUrl: data.disqualifiedUrl || '',
                qualityTerminatedUrl: data.qualityTerminatedUrl || '',
                statusCallbackUrl: data.statusCallbackUrl || '',
                quota: data.quota || '',
                surveyType: data.surveyType || surveyStatus,
                vendorCPI: data.vendorCPI || '',
                preScreenVisibility: data.preScreenVisibility || false,
                marketMode: (Array.isArray(data.selectedMarkets) && data.selectedMarkets.length > 0)
                    ? 'specific'
                    : (isParentSurvey ? 'all' : 'specific'),
                selectedMarkets: data.selectedMarkets || [],
                visibleQuestionIds: Array.isArray(data.visibleQuestionIds) ? data.visibleQuestionIds : [],
                useProxyUrl: data.useProxyUrl || false,
                verisoul: data.verisoul || false,
                verisoul_risk_threshold: data.verisoul_risk_threshold ?? 70,
                verisoul_terminate_count: data.verisoul_terminate_count || 0,
            };
            // data.selectedMarkets is only present when loading an existing partnersSurveys
            // record (update mode). In create mode, getUrlOnTheSurveyBasis returns the Partners
            // model which has no selectedMarkets field. Use a functional updater so that if the
            // user already interacted with the market checkboxes before this async call returned,
            // their selections are preserved instead of being overwritten with defaults.
            const hasPersistedMarkets = data.selectedMarkets !== undefined;
            const hasPersistedVisibleQs = Array.isArray(data.visibleQuestionIds);
            const hasPersistedProxyUrl = data.useProxyUrl !== undefined;
            const hasPersistedVerisoul = data.verisoul !== undefined;
            const hasPersistedThreshold = data.verisoul_risk_threshold !== undefined;
            setFormValues(prev => ({
                ...initialValues,
                marketMode: hasPersistedMarkets ? initialValues.marketMode : prev.marketMode,
                selectedMarkets: hasPersistedMarkets ? initialValues.selectedMarkets : prev.selectedMarkets,
                visibleQuestionIds: hasPersistedVisibleQs ? initialValues.visibleQuestionIds : (prev.visibleQuestionIds || []),
                useProxyUrl: hasPersistedProxyUrl ? initialValues.useProxyUrl : (prev.useProxyUrl || false),
                verisoul: hasPersistedVerisoul ? initialValues.verisoul : (prev.verisoul || false),
                verisoul_risk_threshold: hasPersistedThreshold ? initialValues.verisoul_risk_threshold : (prev.verisoul_risk_threshold ?? 70),
            }));
            initialize(initialValues);
            setStatus(PageStatus.Loaded);
        } catch (error) {
            setError(error.message || 'An error occurred');
            setStatus(PageStatus.Error);
        }
    }, [partnerId, surveyId, initialize]);

    const onSubmitForm = async (event: React.FormEvent) => {
        event.preventDefault();
        // This modal is portalled, but React still bubbles its submit to the
        // survey <form> (Form.tsx) it sits inside, which then saved the whole
        // survey — with the form's stale values (e.g. Client Quota) — on every
        // partner save. Partner saves are self-contained (PartnersTab reloads
        // its own grid), so stop the event here.
        event.stopPropagation();
        setStatus(PageStatus.Submitting);
        try {
            if (formValues.verisoul) {
                const thresholdVal = formValues.verisoul_risk_threshold;
                if (thresholdVal === null || thresholdVal === undefined || thresholdVal === '' || isNaN(Number(thresholdVal)) || Number(thresholdVal) < 0 || Number(thresholdVal) > 100) {
                    throw new Error('Verisoul Risk Threshold Score (%) is required and must be between 0 and 100 when Verisoul is enabled.');
                }
            }

            // Checked on submit, not per keystroke, against the backend's max.
            // A parent has no max — the backend rejects a value below the total
            // of the child markets and its message is shown below.
            if (quotaLimit !== null && formValues.quota !== '' && quotaLimit < parseInt(formValues.quota)) {
                throw new Error(`Quota value can't be greater than ${quotaLimit}`);
            }

            // Coerce blank numeric inputs to null so Postgres doesn't trip
            // 22P02 (invalid_text_representation) when casting "" to float8 /
            // integer. vendorCPI is the FLOAT column on partnersSurveys and is
            // the documented offender; quota is STRING in the model but the
            // input is type=number, so we apply the same rule for consistency.
            const blankToNull = (v: any) => (v === '' || v === undefined ? null : v);
            // When "all markets" is selected, send null for selectedMarkets
            // so the backend cascades to all children.
            const payload = {
                ...formValues,
                vendorCPI: blankToNull(formValues.vendorCPI),
                quota: blankToNull(formValues.quota),
                selectedMarkets: formValues.marketMode === 'specific' ? (formValues.selectedMarkets || []) : null,
                // Persist the per-question visibility array. When the master
                // toggle is off, send [] so the backend doesn't keep stale picks.
                visibleQuestionIds: formValues.preScreenVisibility ? (formValues.visibleQuestionIds || []) : [],
            };
            const response = await modalType == "create" ? PartnersAPI.createOnthebasisofSurvey(payload) : PartnersAPI.updateOnthebasisofSurvey(payload);
            const finalResponse = await response;
            onSubmit(finalResponse);
            setStatus(PageStatus.Submitted);
        } catch (error) {
            setError(error.message || 'An error occurred');
            setStatus(PageStatus.Error);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormValues(prevValues => ({ ...prevValues, [name]: value }));
    };

    if (!show) return null;

    return (
        <Modal
            centered
            size="lg"
            backdrop="static"
            onHide={onClose}
            show={show}
            style={{ zIndex: 1201 }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{modalType == "create" ? "Add" : "Update"} Partner Name: <span className='text-info'>{partnerName}</span></Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '78vh', overflow: 'auto' }}>
                {status === PageStatus.Loading && (
                    <div className="d-flex justify-content-center w-100 p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}
                {status === PageStatus.Error && (
                    <Alert variant="danger">{error}</Alert>
                )}
                <form onSubmit={onSubmitForm}>
                    <div className="form-group">
                        <label htmlFor="successUrl">Completed URL*</label>
                        <input
                            name="successUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.successUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="overQuotaUrl">Quota Full URL*</label>
                        <input
                            name="overQuotaUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.overQuotaUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="disqualifiedUrl">Terminate URL*</label>
                        <input
                            name="disqualifiedUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.disqualifiedUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="qualityTerminatedUrl">Quality Terminate URL*</label>
                        <input
                            name="qualityTerminatedUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.qualityTerminatedUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="statusCallbackUrl">Status Callback URL (optional)</label>
                        <input
                            name="statusCallbackUrl"
                            type="text"
                            className="form-control"
                            placeholder="https://vendor.example.com/webhook"
                            value={formValues.statusCallbackUrl || ''}
                            onChange={handleInputChange}
                        />
                        <small className="text-muted d-block mb-2">
                            Server-to-server webhook URL — we'll POST status events to it. Optional.
                        </small>
                        <label htmlFor="quota">{quotaOptional ? 'Quota' : 'Quota*'}</label>
                        <input
                            name="quota"
                            type="number"
                            className="form-control"
                            placeholder={quotaOptional ? 'Leave blank for the total of the child markets' : 'Enter...'}
                            value={formValues.quota}
                            onChange={handleInputChange}
                            required={!quotaOptional}
                            min={0}
                        />
                        <label htmlFor="vendorCPI">Vendor CPI</label>
                        <input
                            name="vendorCPI"
                            type="number"
                            className="form-control"
                            placeholder="Enter CPI amount"
                            value={formValues.vendorCPI}
                            onChange={handleInputChange}
                            min={0}
                            step="any"
                        />
                        <div className="mt-3">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="preScreenVisibility"
                                    className="custom-control-input"
                                    checked={!!formValues.preScreenVisibility}
                                    onChange={(e) => setFormValues(prev => ({
                                        ...prev,
                                        preScreenVisibility: e.target.checked,
                                        // Drop the picked question list when the master toggle goes off.
                                        visibleQuestionIds: e.target.checked ? (prev.visibleQuestionIds || []) : [],
                                    }))}
                                />
                                <label className="custom-control-label" htmlFor="preScreenVisibility">
                                    Pre-screening visibility to partners
                                </label>
                            </div>
                            <small className="text-muted d-block">Tick the specific pre-screening questions this partner should see.</small>

                            {formValues.preScreenVisibility && (() => {
                                // Filter the per-child prescreener configs by the partner's market
                                // assignment. "specific" => only the selected child IDs; "all" => every child.
                                // selectedMarkets holds child IDs (id-based contract), so gate on cfg.id —
                                // gating on shortName would match nothing now and hide every question.
                                // Empty configs (no markets, or no prescreening anywhere) renders an
                                // explanatory line instead of a blank gap.
                                const eligibleChildIds: string[] | null = (!isParentSurvey || formValues.marketMode === 'all')
                                    ? null
                                    : (formValues.selectedMarkets || []);
                                const visibleConfigs = (childPrescreenConfigs || [])
                                    .filter(cfg => cfg.preQuestionsYN === 'Y' && cfg.questions.length > 0)
                                    .filter(cfg => !eligibleChildIds || eligibleChildIds.includes(cfg.id));

                                if (visibleConfigs.length === 0) {
                                    return (
                                        <div className="mt-2 ml-4 text-muted small">
                                            {isParentSurvey && formValues.marketMode === 'specific' && (eligibleChildIds || []).length === 0
                                                ? 'Pick at least one market below — the question list will appear here.'
                                                : 'No pre-screening questions are configured on this project yet.'}
                                        </div>
                                    );
                                }

                                // Key each checkbox by a COMPOSITE `${childId}::${question_id}`
                                // so the SAME question_id appearing under two markets (e.g. a
                                // shared Profile question under US and UK) toggles independently.
                                // The prefix is the CHILD ID, not shortName: same-country
                                // duplicates (two "JP" children) share a shortName, so a
                                // shortName prefix collided and the two JP children's identical
                                // question shared one checkbox. child.id is unique per instance.
                                //
                                // visibleQuestionIds is opaque to the backend (stored verbatim)
                                // and read nowhere at runtime, so changing the stored format to
                                // id-prefixed composite keys is self-contained to this modal's
                                // save/load — no backend change.
                                //
                                // Backward-compat: legacy records hold bare question_ids (and
                                // older shortName::qid composites). The checked-test still
                                // matches a bare id so old saves render ticked; toggling rewrites
                                // to the id-composite form (migrates forward on next save). A
                                // legacy shortName::qid entry will NOT match the new id-prefixed
                                // key and renders unticked — acceptable: the partner re-confirms
                                // visibility once, which is the desired explicit behavior under
                                // the duplicate-aware model.
                                const ticked = new Set(formValues.visibleQuestionIds || []);
                                const compositeKey = (childId: string, qid: string) => `${childId}::${qid}`;
                                const isTicked = (childId: string, qid: string) =>
                                    ticked.has(compositeKey(childId, qid)) || ticked.has(qid);
                                const toggle = (childId: string, qid: string) => {
                                    const key = compositeKey(childId, qid);
                                    setFormValues(prev => {
                                        const next = new Set(prev.visibleQuestionIds || []);
                                        if (next.has(key)) {
                                            next.delete(key);
                                        } else {
                                            next.add(key);
                                        }
                                        return { ...prev, visibleQuestionIds: Array.from(next) };
                                    });
                                };

                                const allSelectableKeys = visibleConfigs.flatMap(cfg =>
                                    cfg.questions.map(q => compositeKey(cfg.id, q.question_id))
                                );
                                const allSelected = visibleConfigs.length > 0 && visibleConfigs.every(cfg =>
                                    cfg.questions.every(q => isTicked(cfg.id, q.question_id))
                                );

                                const toggleAll = () => {
                                    if (allSelected) {
                                        setFormValues(prev => ({ ...prev, visibleQuestionIds: [] }));
                                    } else {
                                        setFormValues(prev => ({ ...prev, visibleQuestionIds: allSelectableKeys }));
                                    }
                                };

                                // Duplicate detection for the section header: more than one
                                // visible config sharing a shortName → show childProjectName
                                // to tell the two "JP" sections apart.
                                const shortNameCounts = (childPrescreenConfigs || [])
                                    .reduce((acc: Record<string, number>, c) => {
                                        acc[c.shortName] = (acc[c.shortName] || 0) + 1;
                                        return acc;
                                    }, {});

                                return (
                                    <div className="mt-2 ml-4">
                                        <div className="custom-control custom-checkbox mb-3">
                                            <input
                                                type="checkbox"
                                                id="selectAllPreScreen"
                                                className="custom-control-input"
                                                checked={allSelected}
                                                onChange={toggleAll}
                                            />
                                            <label className="custom-control-label font-weight-bold text-primary" htmlFor="selectAllPreScreen">
                                                Select All
                                            </label>
                                        </div>
                                        {visibleConfigs.map(cfg => {
                                            const isDup = (shortNameCounts[cfg.shortName] || 0) > 1;
                                            // Render the PM-typed suffix (e.g. "English") instead
                                            // of the full `${parent}_${cc}_…` stored value, and
                                            // drop the parens entirely when there is no suffix.
                                            // The duplicate-country gate stays: non-duplicate rows
                                            // remain clean as `IN`, only same-market siblings get
                                            // the disambiguating suffix.
                                            const cfgSuffix = extractDisplaySuffix(cfg.childProjectName, projectName || '', cfg.shortName);
                                            const header = isDup && cfgSuffix
                                                ? `${cfg.shortName} (${cfgSuffix})`
                                                : cfg.shortName;
                                            return (
                                            <div key={cfg.id} className="mb-2">
                                                <div className="font-weight-bold small text-uppercase text-muted">
                                                    {header}
                                                </div>
                                                {cfg.questions.map(q => {
                                                    const cid = `vq-${cfg.id}-${q.question_id}`;
                                                    return (
                                                        <div key={cid} className="custom-control custom-checkbox">
                                                            <input
                                                                type="checkbox"
                                                                id={cid}
                                                                className="custom-control-input"
                                                                checked={isTicked(cfg.id, q.question_id)}
                                                                onChange={() => toggle(cfg.id, q.question_id)}
                                                            />
                                                            <label className="custom-control-label" htmlFor={cid}>
                                                                {q.question || q.question_id}
                                                                <span className="ml-2 badge badge-light" style={{ fontSize: '0.7em' }}>
                                                                    {q.type === 'auto' ? 'Profile' : 'Custom'}
                                                                </span>
                                                            </label>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                        {isParentSurvey && (
                        <div className="mt-3">
                            <label>Market Assignment</label>
                            {/* "Add to all markets" is parent-context only. When the modal is
                                opened from a CHILD project's Partners tab (isParentSurvey false),
                                hide it and default to "specific" (set in initial/fetch state). */}
                            {isParentSurvey && (
                            <div className="custom-control custom-radio">
                                <input
                                    type="radio"
                                    id="marketMode-all"
                                    name="marketMode"
                                    className="custom-control-input"
                                    checked={formValues.marketMode === 'all'}
                                    onChange={() => setFormValues(prev => ({ ...prev, marketMode: 'all', selectedMarkets: [] }))}
                                />
                                <label className="custom-control-label" htmlFor="marketMode-all">Add to all markets</label>
                            </div>
                            )}
                            <div className="custom-control custom-radio">
                                <input
                                    type="radio"
                                    id="marketMode-specific"
                                    name="marketMode"
                                    className="custom-control-input"
                                    checked={formValues.marketMode === 'specific'}
                                    onChange={() => setFormValues(prev => ({ ...prev, marketMode: 'specific' }))}
                                />
                                <label className="custom-control-label" htmlFor="marketMode-specific">Add to specific markets</label>
                            </div>
                            {(() => {
                                // Market checkboxes are id-based: selectedMarkets holds child
                                // survey IDs (not shortNames), so same-country duplicates (two
                                // "JP" children) are independently selectable. Only SAVED
                                // children (those with a real id) can be partner-market targets —
                                // partnersSurveys.surveyId is an FK to a persisted child — so
                                // unsaved CREATE picks (no id) are excluded from the list.
                                const selectableMarkets = (childCountries || []).filter((c: any) => !!c.id);
                                // Duplicate detection for labels: more than one selectable child
                                // sharing a shortName → append childProjectName to tell them apart.
                                const snCounts = selectableMarkets.reduce((acc: Record<string, number>, c: any) => {
                                    acc[c.shortName] = (acc[c.shortName] || 0) + 1;
                                    return acc;
                                }, {});
                                if (formValues.marketMode !== 'specific') return null;
                                if (selectableMarkets.length === 0) {
                                    return (
                                        <small className="text-muted d-block mt-1 ml-4">No markets available. Save the survey with countries first.</small>
                                    );
                                }
                                return (
                                    <div className="mt-2 ml-4">
                                        {selectableMarkets.map((c: any) => {
                                            const cid: string = c.id;
                                            const checked = (formValues.selectedMarkets || []).includes(cid);
                                            const isDup = (snCounts[c.shortName] || 0) > 1;
                                            // Mirror the suffix-only render used in the pre-screening
                                            // visibility list above and the Survey Links panel in
                                            // PartnersTab: strip the `${parent}_${cc}_…` prefix and
                                            // drop the parens entirely when there is no suffix.
                                            const marketSuffix = extractDisplaySuffix(c.childProjectName, projectName || '', c.shortName);
                                            const labelText = isDup && marketSuffix
                                                ? `${c.shortName} (${marketSuffix})`
                                                : c.shortName;
                                            return (
                                                <div key={cid} className="custom-control custom-checkbox custom-control-inline">
                                                    <input
                                                        type="checkbox"
                                                        id={`market-${cid}`}
                                                        className="custom-control-input"
                                                        checked={checked}
                                                        onChange={() => {
                                                            const markets = checked
                                                                ? (formValues.selectedMarkets || []).filter(m => m !== cid)
                                                                : [...(formValues.selectedMarkets || []), cid];
                                                            setFormValues(prev => ({ ...prev, selectedMarkets: markets }));
                                                        }}
                                                    />
                                                    <label className="custom-control-label" htmlFor={`market-${cid}`}>{labelText}</label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })()}
                        </div>
                        )}
                        {isUniqueLinks && (
                        <div className="mt-3">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="useProxyUrlCheckbox"
                                    className="custom-control-input"
                                    checked={isUniqueLinks ? true : !!formValues.useProxyUrl}
                                    disabled={isUniqueLinks}
                                    onChange={(e) => setFormValues(prev => ({
                                        ...prev,
                                        useProxyUrl: e.target.checked,
                                    }))}
                                />
                                <label className="custom-control-label" htmlFor="useProxyUrlCheckbox">
                                    Use Proxy URL
                                </label>
                            </div>
                            <small className="text-muted d-block">This partner will receive a Proxy URL.</small>
                        </div>
                        )}
                        <div className="mt-3">
                            <div className="custom-control custom-checkbox">
                                <input
                                    type="checkbox"
                                    id="verisoulCheckbox"
                                    className="custom-control-input"
                                    checked={!!formValues.verisoul}
                                    onChange={(e) => setFormValues(prev => ({
                                        ...prev,
                                        verisoul: e.target.checked,
                                    }))}
                                />
                                <label className="custom-control-label" htmlFor="verisoulCheckbox">
                                    Verisoul
                                </label>
                            </div>
                            {!!formValues.verisoul && (
                                <div className="mt-2 pl-4">
                                    <label htmlFor="verisoulRiskThresholdInput" className="small font-weight-bold text-dark">
                                        Verisoul Risk Threshold Score (%)
                                    </label>
                                    <input
                                        type="number"
                                        id="verisoulRiskThresholdInput"
                                        className="form-control form-control-sm col-md-4"
                                        min={0}
                                        max={100}
                                        value={formValues.verisoul_risk_threshold ?? 70}
                                        onChange={(e) => {
                                            const val = parseInt(e.target.value, 10);
                                            setFormValues(prev => ({
                                                ...prev,
                                                verisoul_risk_threshold: isNaN(val) ? 0 : val,
                                            }));
                                        }}
                                        placeholder="70"
                                    />
                                    <small className="text-muted d-block mt-1">
                                        Requests with a Verisoul risk score &ge; this percentage will be automatically blocked (default: 70%).
                                    </small>
                                </div>
                            )}
                        </div>
                        {/* Survey Status* — this is partnersSurvey.surveyType, the partner's own
                            status on this survey/child, independent of the survey's own
                            surveyType shown on the country tab. Previously gated behind
                            isParentSurvey so it only rendered when the modal was opened from
                            a parent-editing session; ungated so it also renders for a
                            child-direct-edit session (Form.tsx opened on a child's own id). */}
                        <div className="mt-3">
                        <label htmlFor="description">Survey Status*</label><br />
                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType5"
                                name="surveyType5"
                                checked={formValues.surveyType === 'Test'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Test' }))}
                            />
                            <label className="custom-control-label" htmlFor="surveyType5">Test</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType1"
                                name="surveyType1"
                                className="custom-control-input"
                                checked={formValues.surveyType === 'Open'}
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Open' })) }
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType1">Live</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType3"
                                name="surveyType3"
                                checked={formValues.surveyType === 'Hold'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Hold' })) }
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType3">On Hold</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType4"
                                name="surveyType4"
                                checked={formValues.surveyType === 'Wait'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Wait' }))}
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType4">Ids Awaited</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType6"
                                name="surveyType6"
                                checked={formValues.surveyType === 'Close'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Close' }))}
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType6">End</label>
                            </div>
                        </div>
                    </div>
                    <hr />
                    {status === PageStatus.Error && (
                        <Alert variant="danger" className="mt-2">
                            {error}
                        </Alert>
                    )}
                    <div className="d-flex align-items-center mt-2">
                        <button
                            type="submit"
                            disabled={status === PageStatus.Submitting}
                            className="btn btn-primary mr-3"
                        >
                            Submit
                        </button>
                        <button
                            type="button"
                            className="btn btn-light mr-3"
                            onClick={() => {
                                setFormValues({
                                    successUrl: '',
                                    overQuotaUrl: '',
                                    disqualifiedUrl: '',
                                    qualityTerminatedUrl: '',
                                    statusCallbackUrl: '',
                                    quota: '',
                                    verisoul: false,
                                });
                                reset(); // Reset form using redux-form's reset action
                            }}
                        >
                            Reset
                        </button>
                        {status === PageStatus.Submitting && (
                            <Spinner animation="border" variant="primary" />
                        )}
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

const FormRedux = reduxForm < FormValue > ({
    form: 'partnerForm', // Ensure this is unique for each form
    enableReinitialize: true,
})(Form);

const serveFormWithRouter = withRouter(FormRedux);

export { serveFormWithRouter as Form };
